import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { motion } from 'framer-motion';
import { ArrowLeft, Wallet as WalletIcon, Loader2, Phone, ExternalLink } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import MobileNav from '../components/MobileNav';
import { apiFetch } from '../lib/apiClient';
import { isNativeApp, openExternalUrl, PUBLIC_WEB_URL } from '../lib/externalLinks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function parseJsonSafe(response) {
  try {
    const text = await response.clone().text();
    if (!text) return {};
    return JSON.parse(text);
  } catch {
    return {};
  }
}

function WalletTopupPaymentForm({ onSuccess, onError, onCardRejected }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [ready, setReady] = useState(false);
  const completedRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || paying || completedRef.current) return;
    setPaying(true);
    try {
      const submitResult = await elements.submit();
      if (submitResult?.error) {
        throw new Error(submitResult.error.message || 'Please complete payment details');
      }
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (error) throw new Error(error.message || 'Payment failed');
      const intentId = paymentIntent?.id;
      if (!intentId) throw new Error('Missing payment intent');

      // Brief pause so Stripe webhook can credit first (confirm only waits/fallbacks).
      await new Promise((r) => setTimeout(r, 400));

      const response = await apiFetch('/api/wallet/confirm-topup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_intent_id: intentId }),
      });
      const data = await parseJsonSafe(response);
      if (data.code === 'card_country_not_supported') {
        onCardRejected(data.detail);
        return;
      }
      if (!response.ok || !data.success) {
        throw new Error(data.detail || 'Failed to credit wallet');
      }
      if (completedRef.current) return;
      completedRef.current = true;
      onSuccess(data);
    } catch (err) {
      onError(err.message || 'Top-up failed');
    } finally {
      setPaying(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement onReady={() => setReady(true)} />
      <Button type="submit" className="w-full rounded-xl h-12" disabled={!stripe || paying || !ready}>
        {paying ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processing…
          </>
        ) : (
          'Add funds'
        )}
      </Button>
    </form>
  );
}

export default function Wallet() {
  const { user, checkAuth } = useAuth();
  const [config, setConfig] = useState(null);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [creatingIntent, setCreatingIntent] = useState(false);

  const phoneVerified = user?.phone_verified === true;
  // Top-ups happen on the website in the iOS app; the app only shows balance and activity.
  const [webTopupOnly] = useState(isNativeApp);

  const refreshWallet = useCallback(async () => {
    if (!phoneVerified) {
      setLoading(false);
      return;
    }
    try {
      const [balRes, txnRes] = await Promise.all([
        apiFetch('/api/wallet/balance'),
        apiFetch('/api/wallet/transactions?limit=30'),
      ]);
      if (balRes.ok) {
        const bal = await parseJsonSafe(balRes);
        setBalance(bal);
      }
      if (txnRes.ok) {
        const txnData = await parseJsonSafe(txnRes);
        setTransactions(txnData.transactions || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [phoneVerified]);

  useEffect(() => {
    const init = async () => {
      try {
        const [configRes, stripeRes] = await Promise.all([
          fetch(`${API_URL}/api/wallet/config`),
          fetch(`${API_URL}/api/config/stripe`),
        ]);
        if (configRes.ok) setConfig(await parseJsonSafe(configRes));
        const stripeData = await parseJsonSafe(stripeRes);
        if (stripeRes.ok && stripeData.publishable_key) {
          setStripePromise(loadStripe(stripeData.publishable_key));
        }
      } catch (e) {
        console.error(e);
      }
      await refreshWallet();
    };
    init();
  }, [refreshWallet]);

  useEffect(() => {
    if (!webTopupOnly || !phoneVerified) return undefined;
    const refreshAll = () => {
      refreshWallet();
      checkAuth();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshAll();
    };
    document.addEventListener('visibilitychange', onVisible);
    const handles = [];
    if (Capacitor.isNativePlatform()) {
      handles.push(
        CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          if (isActive) refreshAll();
        }),
        Browser.addListener('browserFinished', refreshAll),
      );
    }
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      handles.forEach((h) => Promise.resolve(h).then((l) => l?.remove?.()));
    };
  }, [webTopupOnly, phoneVerified, refreshWallet, checkAuth]);

  const openWebTopup = () => {
    openExternalUrl(`${PUBLIC_WEB_URL}/wallet`).catch(() => {
      toast.error('Could not open the SkipdQ website');
    });
  };

  const handleSelectPreset = async (amountCents) => {
    setSelectedPreset(amountCents);
    setClientSecret('');
    setCreatingIntent(true);
    try {
      const response = await apiFetch('/api/wallet/topup-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_cents: amountCents }),
      });
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(data.detail || 'Failed to start top-up');
      setClientSecret(data.client_secret || '');
    } catch (err) {
      toast.error(err.message || 'Top-up failed');
      setSelectedPreset(null);
    } finally {
      setCreatingIntent(false);
    }
  };

  const handleTopupSuccess = async (data) => {
    toast.success(`Wallet updated — CA$ ${(data.wallet_available_balance ?? data.wallet_balance ?? 0).toFixed(2)} available`);
    setClientSecret('');
    setSelectedPreset(null);
    await checkAuth();
    await refreshWallet();
  };

  const formatTxn = (txn) => {
    const labels = {
      topup: 'Top-up',
      bonus: 'Bonus',
      order_debit: 'Order payment',
      order_refund: 'Refund',
    };
    const amt = txn.amount_cents / 100;
    const sign = amt >= 0 ? '+' : '';
    return {
      label: labels[txn.type] || txn.type,
      amount: `${sign}CA$ ${Math.abs(amt).toFixed(2)}`,
      positive: txn.amount_cents >= 0,
      date: new Date(txn.created_at).toLocaleString(),
    };
  };

  const bonusPercent = config?.wallet_bonus_percent ?? 0;

  return (
    <div className="min-h-screen bg-background pb-safe">
      <Header />
      <div className="max-w-lg mx-auto px-4 pt-20 pb-24">
        <Link
          to="/profile"
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to profile
        </Link>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <WalletIcon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">SkipdQ Wallet</h1>
              <p className="text-sm text-muted-foreground">One balance per verified phone</p>
            </div>
          </div>

          {!phoneVerified ? (
            <div className="bg-card rounded-2xl p-6 border text-center">
              <Phone className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium mb-2">Verify your phone first</p>
              <p className="text-sm text-muted-foreground mb-4">
                Wallet funds are tied to your mobile number so every account with that number shares the same balance.
              </p>
              <p className="text-sm text-muted-foreground">
                Complete phone verification when you sign in, then return here.
              </p>
            </div>
          ) : (
            <>
              <div
                className="bg-[#111] text-white rounded-2xl p-6 mb-6 border border-primary/20"
                data-testid="wallet-balance-card"
              >
                <p className="text-sm text-gray-400 uppercase tracking-wider mb-1">Available balance</p>
                {loading ? (
                  <Loader2 className="w-8 h-8 animate-spin mt-2" />
                ) : (
                  <>
                    <p className="text-4xl font-bold font-mono">
                      CA$ {(balance?.wallet_available_balance ?? balance?.wallet_balance ?? user?.wallet_balance ?? 0).toFixed(2)}
                    </p>
                    {(balance?.wallet_held_balance ?? 0) > 0 && (
                      <p className="text-sm text-amber-400 mt-2">
                        CA$ {balance.wallet_held_balance.toFixed(2)} held for pending orders · total CA$ {(balance.wallet_balance ?? 0).toFixed(2)}
                      </p>
                    )}
                  </>
                )}
                {bonusPercent > 0 && (
                  <p className="text-sm text-green-400 mt-2">
                    {bonusPercent}% bonus on every top-up
                  </p>
                )}
              </div>

              {webTopupOnly ? (
              <div className="bg-card rounded-2xl p-4 border mb-6" data-testid="wallet-web-topup">
                <h2 className="font-semibold mb-3">Add funds</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Wallet top-ups are done on the SkipdQ website. Sign in there with the same account
                  and your balance will update here automatically.
                </p>
                <Button
                  type="button"
                  className="w-full rounded-xl h-12"
                  onClick={openWebTopup}
                  data-testid="wallet-web-topup-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Add funds on the SkipdQ website
                </Button>
              </div>
              ) : (
              <div className="bg-card rounded-2xl p-4 border mb-6">
                <h2 className="font-semibold mb-3">Add funds</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Pay once with card — use your balance on orders (no extra Stripe fee per order).
                </p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {(config?.presets || []).map((p) => (
                    <Button
                      key={p.amount_cents}
                      type="button"
                      variant={selectedPreset === p.amount_cents ? 'default' : 'outline'}
                      className="h-12"
                      disabled={creatingIntent}
                      onClick={() => handleSelectPreset(p.amount_cents)}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
                {selectedPreset && bonusPercent > 0 && (
                  <p className="text-xs text-muted-foreground mb-3">
                    You will receive CA${' '}
                    {(
                      (selectedPreset / 100) *
                      (1 + bonusPercent / 100)
                    ).toFixed(2)}{' '}
                    credit ({bonusPercent}% bonus included).
                  </p>
                )}
                {creatingIntent && (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                {clientSecret && stripePromise && !creatingIntent && (
                  <>
                    <p className="text-xs text-muted-foreground mb-3" data-testid="canadian-cards-note">
                      Only Canadian-issued cards are accepted.
                    </p>
                    <Elements
                      key={clientSecret}
                      stripe={stripePromise}
                      options={{
                        clientSecret,
                        appearance: { variables: { fontSizeBase: '16px' } },
                      }}
                    >
                      <WalletTopupPaymentForm
                        onSuccess={handleTopupSuccess}
                        onError={(msg) => toast.error(msg)}
                        onCardRejected={(msg) => {
                          toast.error(msg, { duration: 10000 });
                          // The authorization was voided; pick an amount again to start a fresh payment.
                          setClientSecret('');
                          setSelectedPreset(null);
                        }}
                      />
                    </Elements>
                  </>
                )}
              </div>
              )}

              <div className="bg-card rounded-2xl p-4 border">
                <h2 className="font-semibold mb-3">Recent activity</h2>
                {transactions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No transactions yet</p>
                ) : (
                  <ul className="space-y-3">
                    {transactions.map((txn) => {
                      const row = formatTxn(txn);
                      return (
                        <li
                          key={txn.transaction_id}
                          className="flex justify-between items-start text-sm border-b border-border pb-2 last:border-0"
                        >
                          <div>
                            <p className="font-medium">{row.label}</p>
                            <p className="text-xs text-muted-foreground">{row.date}</p>
                          </div>
                          <span
                            className={
                              row.positive ? 'text-green-600 font-medium' : 'text-foreground font-medium'
                            }
                          >
                            {row.amount}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </motion.div>
      </div>
      <MobileNav />
    </div>
  );
}
